// src/composables/store/master/useAuthStore.js

import { apiInstance } from "@/utils/axios";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

const MASTER_LOCALSTORAGE = {
  ACCESS_TOKEN: "MASTER_ACCESS_TOKEN",
  USER_ID: "MASTER_USER_ID",
  ROLE: "MASTER_ROLE",
  NAME: "MASTER_NAME",
};

function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = window.atob(base64);
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export const useMasterAuthStore = defineStore("masterAuthStore", () => {
  // ---------------------------
  // State
  // ---------------------------
  const accessToken = ref(
    localStorage.getItem(MASTER_LOCALSTORAGE.ACCESS_TOKEN) || null
  );
  const userId = ref(localStorage.getItem(MASTER_LOCALSTORAGE.USER_ID) || null);
  const user = ref(null);

  const isInitialized = ref(false);

  const signin = ref({
    user: {
      email: "",
      password: "",
      remember_me: false,
    },
    isLoading: false,
    response: null,
  });

  const isChangePasswordLoading = ref(false);
  const isForgetPasswordLoading = ref(false);
  const isResetPasswordLoading = ref(false);

  // ---------------------------
  // Getters
  // ---------------------------
  const isAuthenticated = computed(() => !!accessToken.value);
  const isSigninLoading = computed(() => signin.value.isLoading);

  const authHeaders = computed(() => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: accessToken.value
      ? `Bearer ${accessToken.value}`
      : undefined,
  }));

  // ---------------------------
  // Helpers
  // ---------------------------
  function setAccessToken(token) {
    accessToken.value = token;
    localStorage.setItem(MASTER_LOCALSTORAGE.ACCESS_TOKEN, token);

    // NOTE:
    // This sets a global Authorization header on the shared axios instance.
    // If you want STRICT isolation between admin/user/contractor/master sessions,
    // remove the line below and always pass headers per-request.
    apiInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const tokenData = parseJwt(token);
    if (tokenData?.user_id) {
      userId.value = String(tokenData.user_id);
      localStorage.setItem(MASTER_LOCALSTORAGE.USER_ID, userId.value);
    }
  }

  function clearAuthData() {
    accessToken.value = null;
    userId.value = null;
    user.value = null;

    localStorage.removeItem(MASTER_LOCALSTORAGE.ACCESS_TOKEN);
    localStorage.removeItem(MASTER_LOCALSTORAGE.USER_ID);
    localStorage.removeItem(MASTER_LOCALSTORAGE.ROLE);
    localStorage.removeItem(MASTER_LOCALSTORAGE.NAME);

    // Optional: do NOT delete global auth header if you support multiple portals concurrently
    // delete apiInstance.defaults.headers.common["Authorization"];
  }

  function updateSigninUserData(payload) {
    signin.value.user = { ...signin.value.user, ...payload };
  }

  // ---------------------------
  // API Actions
  // ---------------------------
  async function getAuthenticatedUser() {
    // ✅ Your backend route is: GET /api/master/me
    const response = await apiInstance.get("/api/master/me", {
      headers: authHeaders.value,
    });
    return response.data;
  }

  async function initializeAuth() {
    if (isInitialized.value) return;

    try {
      const token = localStorage.getItem(MASTER_LOCALSTORAGE.ACCESS_TOKEN);

      if (!token) {
        isInitialized.value = true;
        return;
      }

      // Basic expiry check (optional but helps avoid spam calls)
      const tokenData = parseJwt(token);
      const now = Math.floor(Date.now() / 1000);

      if (tokenData?.exp && tokenData.exp <= now) {
        clearAuthData();
        isInitialized.value = true;
        return;
      }

      setAccessToken(token);

      // ✅ pull master user from backend
      const me = await getAuthenticatedUser();
      user.value = me;

      // (optional) store basic info if backend returns it
      if (me?.role) localStorage.setItem(MASTER_LOCALSTORAGE.ROLE, me.role);
      if (me?.name) localStorage.setItem(MASTER_LOCALSTORAGE.NAME, me.name);
    } catch (error) {
      // If /api/master/me fails -> treat as logged out
      clearAuthData();
      throw error;
    } finally {
      isInitialized.value = true;
    }
  }

  async function signIn() {
    signin.value.isLoading = true;

    try {
      // ✅ Your backend route for login should be: POST /api/master/auth/login
      const response = await apiInstance.post(
        "/api/master/auth/login",
        {
          email: signin.value.user.email,
          password: signin.value.user.password,
          remember_me: signin.value.user.remember_me,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      signin.value.response = response.data;

      const { access_token } = response.data || {};
      if (!access_token)
        throw new Error("No access token received from /api/master/auth/login");

      setAccessToken(access_token);

      // ✅ immediately load master user
      const me = await getAuthenticatedUser();
      user.value = me;

      if (me?.role) localStorage.setItem(MASTER_LOCALSTORAGE.ROLE, me.role);
      if (me?.name) localStorage.setItem(MASTER_LOCALSTORAGE.NAME, me.name);

      return response.data;
    } catch (error) {
      signin.value.response = null;
      clearAuthData();
      throw error;
    } finally {
      signin.value.isLoading = false;
    }
  }

  async function logout(skipApiCall = false) {
    try {
      if (!skipApiCall && accessToken.value) {
        // ✅ Your backend route is: POST /api/master/auth/logout
        await apiInstance.post(
          "/api/master/auth/logout",
          {},
          {
            headers: authHeaders.value,
          }
        );
      }
    } finally {
      clearAuthData();
    }
  }

  async function changePassword(payload) {
    try {
      isChangePasswordLoading.value = true;

      // ✅ Your backend route is: POST /api/master/auth/change-password
      const response = await apiInstance.post(
        "/api/master/auth/change-password",
        {
          current_password: payload.current_password,
          new_password: payload.new_password,
          new_password_confirmation: payload.new_password_confirmation,
        },
        {
          headers: authHeaders.value,
        }
      );

      return response.data;
    } finally {
      isChangePasswordLoading.value = false;
    }
  }

  async function forgetPassword(email) {
    try {
      isForgetPasswordLoading.value = true;

      // If you DO have this route in backend:
      // POST /api/master/auth/forget-password
      const response = await apiInstance.post(
        "/api/master/auth/forget-password",
        { email },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return response.data;
    } finally {
      isForgetPasswordLoading.value = false;
    }
  }

  async function resetPassword(payload) {
    try {
      isResetPasswordLoading.value = true;

      // If you DO have this route in backend:
      // POST /api/master/auth/reset-password
      const response = await apiInstance.post(
        "/api/master/auth/reset-password",
        {
          token: payload.token,
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return response.data;
    } finally {
      isResetPasswordLoading.value = false;
    }
  }

  return {
    // state
    accessToken,
    userId,
    user,
    signin,
    isInitialized,
    isChangePasswordLoading,
    isForgetPasswordLoading,
    isResetPasswordLoading,

    // getters
    isAuthenticated,
    isSigninLoading,

    // actions
    initializeAuth,
    updateSigninUserData,
    signIn,
    logout,
    getAuthenticatedUser,
    changePassword,
    forgetPassword,
    resetPassword,

    // helpers
    clearAuthData,
    setAccessToken,
  };
});
